import { Link, useNavigate } from "@tanstack/react-router";
import type { LoginNextStepV1 } from "@strust/contracts";
import { link } from "@strust/ui/recipes";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { assignLocation } from "../../../shared/navigation.ts";
import { isReleaseFlagOn } from "../../../shared/release-flags.ts";
import { useLogin, useSsoStart } from "../api/session.ts";
import { LoginScreen } from "../components/login-screen.tsx";
import { loginMessages, loginStepMessages } from "../messages.ts";
import { LoginStep } from "./login-step.tsx";
import { describeError, errorCode } from "../model/error-message.ts";
import { describeSsoRedirectError } from "../model/sso-error.ts";

const forgotPasswordLinkClass = link();

export interface LoginPageProps {
  /** `?error=` set by the SSO callback redirect. */
  readonly redirectError?: string | undefined;
  /**
   * `?reason=session_ended` set when a session ended or expired, `signed_out` after a logout,
   * `password_reset` after a self-service password reset, `password_reset_unfinished` when the reset
   * set the new password but could not be finished (a `503` with `password_changed`).
   */
  readonly reason?: string | undefined;
}

export function LoginPage({ redirectError, reason }: LoginPageProps) {
  const intl = useIntl();
  const navigate = useNavigate();
  const login = useLogin();
  const ssoStart = useSsoStart();
  const [redirecting, setRedirecting] = useState(false);
  // The step a right password still needs (change add-console-account-settings, design D5).
  const [step, setStep] = useState<LoginNextStepV1 | undefined>();
  const [stepExpired, setStepExpired] = useState(false);
  const goHome = useCallback(() => {
    void navigate({ to: "/" });
  }, [navigate]);
  const restart = useCallback(
    (reason: "expired" | "back") => {
      login.reset();
      setStep(undefined);
      setStepExpired(reason === "expired");
    },
    [login],
  );

  const failure = login.error ?? ssoStart.error;
  const error = failure
    ? describeError(intl, failure)
    : redirectError
      ? describeSsoRedirectError(intl, redirectError)
      : undefined;
  const info =
    reason === "session_ended"
      ? intl.formatMessage(loginMessages.sessionEnded)
      : reason === "signed_out"
        ? intl.formatMessage(loginMessages.signedOut)
        : reason === "password_reset"
          ? intl.formatMessage(loginMessages.passwordReset)
          : reason === "password_reset_unfinished"
            ? intl.formatMessage(loginMessages.passwordResetUnfinished)
            : undefined;

  if (step !== undefined)
    return (
      <LoginStep
        key={step}
        step={step}
        onSignedIn={goHome}
        onRestart={restart}
        onNextStep={setStep}
      />
    );

  return (
    <LoginScreen
      pending={login.isPending || ssoStart.isPending || redirecting}
      redirecting={redirecting}
      error={stepExpired && !failure ? intl.formatMessage(loginStepMessages.expired) : error}
      info={info}
      preferSso={errorCode(login.error) === "sso_required"}
      {...(isReleaseFlagOn("iam.password-reset")
        ? {
            forgotPassword: (
              <Link to="/forgot-password" className={forgotPasswordLinkClass}>
                {intl.formatMessage(loginMessages.forgotPassword)}
              </Link>
            ),
          }
        : {})}
      onSubmit={(credentials) => {
        ssoStart.reset();
        setStepExpired(false);
        login.mutate(credentials, {
          onSuccess: (outcome) => {
            if (outcome.kind === "step") setStep(outcome.step);
            else goHome();
          },
        });
      }}
      onSso={(email) => {
        login.reset();
        ssoStart.mutate(
          { email },
          {
            onSuccess: ({ authorization_url }) => {
              setRedirecting(true);
              assignLocation(authorization_url);
            },
          },
        );
      }}
    />
  );
}
